// Extracts structured-data signals from blog content for AEO schema injection.
// These drive the FAQPage, HowTo, and Speakable schema types in BlogPost.tsx.

import { stripInlineMarkdown } from "@/lib/markdownText";
import { toPlainText } from "@/lib/contentStyleRules";

// Step text is shown as plain text and copied into HowTo schema, so it is
// converted from markdown line by line; list items read as one comma list.
export function stepText(lines: string[], max = 400): string {
  let out = "";
  let prevItem = false;
  for (const line of lines) {
    const text = toPlainText(line);
    if (!text) continue;
    const item = /^\s*(?:[-*+•]|\d+[.)])\s+/.test(line);
    if (!out) out = text;
    else if (item)
      out += prevItem
        ? `, ${text}`
        : out.endsWith(":")
          ? ` ${text}`
          : `: ${text}`;
    else out += (/[.!?:]$/.test(out) ? " " : ". ") + text;
    prevItem = item;
  }
  if (out.length <= max) return out;
  const cut = out.slice(0, max);
  const space = cut.lastIndexOf(" ");
  return (space > 0 ? cut.slice(0, space) : cut).replace(/[,;:\s]+$/, "") + "…";
}

// Looks for ## or ### headings that end with "?" — that's the signal for a Q&A pair.
// Anything under 20 chars is usually a nav link or fragment, not a real answer.
export function extractFaqs(
  content: string,
): Array<{ question: string; answer: string }> {
  const lines = content.split("\n");
  const faqs: Array<{ question: string; answer: string }> = [];
  let currentQ: string | null = null;
  let answerLines: string[] = [];

  const flush = () => {
    if (currentQ && answerLines.length) {
      const answer = stripInlineMarkdown(
        answerLines.join(" ").replace(/[#>]/g, ""),
      );
      if (answer.length > 20)
        faqs.push({ question: currentQ, answer: answer.slice(0, 500) });
    }
    currentQ = null;
    answerLines = [];
  };

  for (const line of lines) {
    const h = line.match(/^(#{2,3})\s+(.+)/);
    if (h) {
      flush();
      const text = stripInlineMarkdown(h[2]);
      if (text.endsWith("?")) currentQ = text;
      continue;
    }
    if (currentQ && line.trim() && !line.startsWith("::"))
      answerLines.push(line.trim());
  }
  flush();
  return faqs;
}

// Numbered list items are the cleanest HowTo signal — "1. Do X" maps directly to a step.
// Falls back to ## headings ONLY when the content has no numbered list at all — when a
// numbered list IS present, a heading afterward (e.g. "## FAQ") means the how-to section
// has ended, not that the FAQ/next-steps heading is itself a step.
export function extractHowToSteps(
  content: string,
): Array<{ name: string; text: string }> {
  const lines = content.split("\n");
  // Whole-document lookahead: does a numbered list exist ANYWHERE? Governs whether
  // headings before the list starts (e.g. a "## Practical how-to" section title) get
  // ignored versus, when no numbered list exists at all, treated as the steps themselves.
  const hasNumberedStep = lines.some((l) => /^\d+\.\s+/.test(l));
  const steps: Array<{ name: string; text: string }> = [];
  let currentStep: { name: string; lines: string[] } | null = null;
  // Running state: have we actually reached the numbered list yet during iteration?
  let numberedStarted = false;

  for (const line of lines) {
    // Group 1 = bold lead-in if present, group 2 = any remaining text on the same line —
    // "1. **Do X** because Y" previously dropped "because Y" entirely.
    const numbered = line.match(
      /^\d+\.\s+(?:\*{1,2}([^*\n]+)\*{1,2})?\s*(.*)$/,
    );
    const heading = line.match(/^#{2,3}\s+(.+)/);

    if (numbered) {
      numberedStarted = true;
      if (currentStep)
        steps.push({
          name: currentStep.name,
          text: stepText(currentStep.lines),
        });
      const boldLeadIn = numbered[1]?.trim();
      const rest = numbered[2]?.trim();
      currentStep = {
        name: stripInlineMarkdown(boldLeadIn || rest || ""),
        lines: [],
      };
      if (rest && boldLeadIn) currentStep.lines.push(stripInlineMarkdown(rest));
      continue;
    }

    if (heading) {
      if (numberedStarted) {
        // A heading after the list has already started (e.g. "## FAQ") means the
        // how-to section has ended — stop entirely rather than let unrelated
        // sections leak in as fake steps.
        if (currentStep)
          steps.push({
            name: currentStep.name,
            text: stepText(currentStep.lines),
          });
        currentStep = null;
        break;
      }
      if (!hasNumberedStep) {
        // No numbered list anywhere in the content — fall back to headings as steps.
        if (currentStep)
          steps.push({
            name: currentStep.name,
            text: stepText(currentStep.lines),
          });
        currentStep = {
          name: stripInlineMarkdown(heading[1].trim()),
          lines: [],
        };
      }
      // else: a heading before the numbered list starts (e.g. the section title) — ignore.
      continue;
    }

    if (currentStep && line.trim() && !line.startsWith("::")) {
      currentStep.lines.push(line.trim());
    }
  }
  if (currentStep)
    steps.push({ name: currentStep.name, text: stepText(currentStep.lines) });
  return steps.filter((s) => s.name.length > 3).slice(0, 12);
}

// Looks for a ## or ### heading with "checklist" anywhere in its text, then takes the
// numbered or bulleted list items directly under it (stops at the next heading). Real
// checklist sections exist in roughly 40% of generated posts — when absent, this
// returns an empty array and the interactive checklist module simply doesn't render.
export function extractChecklist(
  content: string,
  headingOverride?: string,
): string[] {
  const lines = content.split("\n");
  const items: string[] = [];
  let inChecklist = false;
  const overrideNorm = headingOverride?.trim().toLowerCase();

  for (const line of lines) {
    const heading = line.match(/^(#{2,3})\s+(.+)/);
    if (heading) {
      if (inChecklist) break; // next heading ends the checklist section
      // Some GeckoCheck briefs specify an exact H2 name for what is
      // functionally a checklist (e.g. "Red Flags and Good Signs") without
      // the literal word "checklist" in it. headingOverride is a per-post
      // opt-in (blog_posts.geckocheck_event_names.checklist_heading) for
      // exactly that case — it never changes default behavior for posts
      // that don't set it.
      const text = heading[2].trim().toLowerCase();
      if (
        /checklist/i.test(heading[2]) ||
        (overrideNorm && text === overrideNorm)
      )
        inChecklist = true;
      continue;
    }
    if (!inChecklist) continue;
    const item = line.match(/^\s*(?:\d+[\.\)]|[-*])\s+(.+)/);
    if (item) {
      const text = stripInlineMarkdown(item[1].trim());
      if (text.length > 3) items.push(text.slice(0, 200));
    }
  }
  return items.slice(0, 15);
}

// Looks for a ## or ### heading with "glossary" anywhere in its text, then takes
// **Term**\n\nDefinition pairs directly under it (stops at the next heading). Mirrors
// extractChecklist's heading-detection convention. Terms are bolded key terms in the
// markdown source, definitions are the plain-paragraph line immediately after.
export function extractGlossary(
  content: string,
): Array<{ term: string; definition: string }> {
  const lines = content.split("\n").map((l) => l.trim());
  const entries: Array<{ term: string; definition: string }> = [];
  let inGlossary = false;
  let pendingTerm: string | null = null;

  for (const line of lines) {
    const heading = line.match(/^(#{2,3})\s+(.+)/);
    if (heading) {
      if (inGlossary) break;
      if (/glossary/i.test(heading[2])) inGlossary = true;
      continue;
    }
    if (!inGlossary || !line) continue;
    const termMatch = line.match(/^\*\*(.+?)\*\*$/);
    if (termMatch) {
      pendingTerm = termMatch[1].trim();
      continue;
    }
    if (pendingTerm) {
      const definition = stripInlineMarkdown(line);
      if (definition.length > 3) {
        entries.push({
          term: pendingTerm,
          definition: definition.slice(0, 400),
        });
      }
      pendingTerm = null;
    }
  }
  return entries.slice(0, 20);
}

// FAQPage needs ≥3 pairs to be eligible for rich results (Google's minimum).
// HowTo requires both a "how to" title AND actual steps — title alone isn't enough.
// Speakable fires on any FAQ content — even one answer is voice-assistant worthy.
export function getAeoTypes(
  title: string,
  faqs: Array<{ question: string; answer: string }>,
  steps: Array<{ name: string; text: string }>,
): string[] {
  const types = ["Article"];
  if (faqs.length >= 3) types.push("FAQPage");
  if (/^how to /i.test(title) && steps.length > 0) types.push("HowTo");
  if (faqs.length > 0) types.push("Speakable");
  return types;
}
